const STORAGE_KEY = "intended_redirect";

const BLOCKED_PREFIXES = ["/login", "/portal", "/admin/login"];

/** In-memory lock so Login.goHome and PublicRoute agree on one destination. */
let lockedDestination = null;

/**
 * Normalize and validate a post-login redirect path (relative only).
 * Rejects external URLs and auth pages.
 */
export function sanitizeIntendedPath(raw) {
  if (!raw || typeof raw !== "string") return null;

  let value = raw.trim();
  try {
    value = decodeURIComponent(value);
  } catch {
    // keep raw
  }

  if (/^https?:\/\//i.test(value) || value.startsWith("//")) {
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) return null;
      value = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  if (!value.startsWith("/")) return null;

  const pathOnly = value.split("?")[0].split("#")[0] || "/";
  if (pathOnly === "/") return null;
  if (BLOCKED_PREFIXES.some((p) => pathOnly === p || pathOnly.startsWith(`${p}/`))) {
    return null;
  }

  return value;
}

export function getIntendedRedirect() {
  try {
    return sanitizeIntendedPath(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function saveIntendedRedirect(raw) {
  const path = sanitizeIntendedPath(raw);
  if (!path) return null;
  lockedDestination = null;
  try {
    localStorage.setItem(STORAGE_KEY, path);
  } catch {
    // ignore quota / private mode
  }
  return path;
}

export function clearIntendedRedirect() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Resolve post-auth destination once so Login and PublicRoute cannot diverge.
 */
export function lockPostAuthDestination(fallback = "/") {
  if (lockedDestination) return lockedDestination;
  lockedDestination = getIntendedRedirect() || fallback || "/";
  clearIntendedRedirect();
  return lockedDestination;
}

export function resetPostAuthDestinationLock() {
  lockedDestination = null;
}

/**
 * Consume saved intended path, or fall back to role home.
 * @deprecated Prefer lockPostAuthDestination for post-login navigation.
 */
export function consumeIntendedRedirect(fallback = "/") {
  return lockPostAuthDestination(fallback);
}
